import { styled } from "@mui/material/styles";
import { Box, Typography, Button } from "@mui/material";
import colors from "../../theme/Colours";
import theme from "../../theme/Theme";

interface RootContainerProps {
  image: string;
}

export const RootContainer = styled(Box, {
  shouldForwardProp: (prop) => !["image"].includes(prop as string),
})<RootContainerProps>(({ theme }) => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  overflow: "hidden",
  width: "360px",
  height: "420px",
  marginTop: theme.spacing(5),
  marginLeft: "auto",
  marginRight: "auto",
  backgroundColor: "#FFFFFF",
  border: "1px solid #e0e0e0",
  borderRadius: "8px",
  padding: "16px",
  boxSizing: "border-box",
  cursor: "pointer",
  transition: "box-shadow 0.2s ease",
  flexShrink: 0,
  "&:hover": {
    boxShadow: "0 12px 24px rgba(0,0,0,0.12)",
  },
  [theme.breakpoints.down("lg")]: {
    width: "320px",
    height: "380px",
  },
  [theme.breakpoints.down("md")]: {
    width: "280px",
    height: "340px",
  },
  [theme.breakpoints.down("sm")]: {
    width: "260px",
    height: "320px",
  },
}));

export const ImageBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "image",
})<{ image: string }>(({ image }) => ({
  width: "100%",
  height: "240px",
  backgroundImage: `url(${image})`,
  backgroundSize: "contain",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  flexShrink: 0,
}));

export const TextBox = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  paddingTop: theme.spacing(1.5),
}));

export const Caption = styled(Typography)(() => ({
  color: colors.outlinedBtnColor,
  fontWeight: 600,
  fontSize: 14,
}));

export const Title = styled(Typography)(() => ({
  fontFamily: "DM Sans, sans-serif",
  fontWeight: 400,
  fontSize: "20px",
  color: colors.black,
  marginTop: 4,
  marginBottom: 0,
}));

export const Description = styled(Typography)(() => ({
  marginTop: 8,
  fontSize: 18,
  color: colors.mediumGrey1,
}));

export const ProductDetailsBox = styled(Box)(() => ({
  display: "flex",
  justifyContent: "space-between",
  gap: "16px",
  marginTop: 8,
  fontFamily: "Gotham HTF, sans-serif",
  fontWeight: 400,
  color: colors.clickTextBlack,
  "& span": {
    fontSize: 14,
    color: colors.clickTextBlack,
  },
}));

export const ButtonContainer = styled(Box)(() => ({
  display: "block",
  marginTop: "auto",
  width: "100%",
  paddingTop: "12px",
}));

export const ExploreButton = styled(Button)(() => ({
  border: `1px solid ${colors.primaryButtonOrange}`,
  color: colors.primaryButtonOrange,
  backgroundColor: "transparent",
  borderRadius: 4,
  width: "100%",
  height: "36px",
  boxShadow: "none",
  textTransform: "none",
  fontSize: 16,
  fontWeight: 500,
  "&:hover": {
    backgroundColor: colors.primaryButtonOrangeLight,
    borderColor: colors.primaryButtonOrange,
    boxShadow: "none",
  },
}));

export const ArrowButton = styled(Box)(({ theme }) => ({
  cursor: "pointer",
  borderRadius: "50%",
  transition: "all 0.3s ease",
  [theme.breakpoints.down("sm")]: {
    padding: "10px 16px",
  },
}));

export const SectionWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: colors.productBackground,
  paddingBottom: theme.spacing(6),
  gap: theme.spacing(2),
  width: "100%",
  overflow: "hidden",
}));

export const CardsRow = styled("div")(({ theme }) => ({
  display: "flex",
  gap: "30px",
  width: "100%",
  maxWidth: "calc(100vw - 160px)", // Account for arrow button padding
  overflow: "hidden",
  justifyContent: "center",
  [theme.breakpoints.down("lg")]: {
    gap: "20px",
    maxWidth: "calc(100vw - 120px)",
  },
  [theme.breakpoints.down("md")]: {
    gap: "15px",
    maxWidth: "calc(100vw - 100px)",
  },
}));

export const ArrowButtonWrapper = styled("button")(
  ({ disabled, theme }: { disabled: boolean; theme?: any }) => ({
    border: "none",
    background: "transparent",
    cursor: disabled ? "default" : "pointer",
    opacity: disabled ? 0.3 : 1,
    display: "flex",
    alignItems: "center",
    padding: "40px",
    flexShrink: 0,
    [theme?.breakpoints?.down("lg")]: {
      padding: "30px",
    },
    [theme?.breakpoints?.down("md")]: {
      padding: "20px",
    },
    [theme?.breakpoints?.down("sm")]: {
      padding: "15px",
    },
  })
);

export const DotsWrapper = styled("div")({
  display: "flex",
  justifyContent: "center",
  marginTop: "24px",
  gap: "8px",
});

export const Dot = styled("button")<{ active: boolean }>(({ active }) => ({
  width: 10,
  height: 10,
  borderRadius: "50%",
  border: "none",
  padding: 0,
  cursor: "pointer",
  backgroundColor: active
    ? colors.primaryButtonOrange
    : "rgba(255, 140, 0, 0.8)",
}));

export const BannerWrap = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  width: "100%",
  maxWidth: "100vw",
  overflow: "hidden",
  justifyContent: "center",
}));
